/* eslint-disable no-undef */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosRetry, { IAxiosRetryConfig } from 'axios-retry';
import { Dispatch } from 'redux';
import config from '../../../../app.config.json';
import logging from '../services/logging';
import { NetworkContext } from '../types/network';
import { getAuthHeaders, setClientToken } from '../utils/auth';
import getUserAgent from '../utils/getUserAgent';
import isReactNative from '../utils/isReactNative';
import logout from '../utils/logout';
import { getItem } from './storage.secure';

export const API_URL =
  typeof document === 'undefined' || process?.env?.JEST_WORKER_ID ? config.apiUrl : `${window.location.origin}/_wapi`;

const RETRY_DELAY = 1000;
export const RETRY_CONFIG: IAxiosRetryConfig = {
  retries: process?.env?.API_URL ? 1 : 5,
  // increment each retry request by another second
  retryDelay: (retryCount) => retryCount * RETRY_DELAY,
  // allows retry whenever cloudflare returns 429 error for too many requests
  retryCondition: (error) => {
    return (error && error.response && [429, 503, 504].includes(error.response.status)) || false;
  },
};

if (!process?.env?.JEST_WORKER_ID) {
  axiosRetry(axios, RETRY_CONFIG);
}

const axiosInstance = async <T = any>(
  requestConfig: AxiosRequestConfig,
  dispatch: Dispatch | undefined,
  ctx?: NetworkContext,
  retried?: boolean,
): Promise<AxiosResponse<T>> => {
  const url = axios.getUri(requestConfig);
  const headers = await getAuthHeaders(url, ctx);
  const userAgent = getUserAgent(ctx);

  logging.logCrumb({
    message: 'request',
    metaData: {
      url,
      method: requestConfig.method,
    },
  });

  try {
    const result = await axios({
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        ...userAgent,
        ...headers,
      },
      withCredentials: !isReactNative(),
    });

    if (result.headers['x-client-token']) {
      const token = await getItem('token', ctx);
      await setClientToken(result.headers['x-client-token'], token?.emailHashes, dispatch, ctx);
    }

    return result;
  } catch (error: any) {
    if (error.response) {
      if (error.response.status >= 500) {
        logging.logError(new Error(`API error ${error.response.status} on ${url}`));
      } else if (error.response.status >= 401) {
        logging.logError(new Error(`API error ${error.response.status} on ${url}`));
      } else {
        logging.logError(new Error(`API error ${error.response.status} on ${url}`));
      }

      if ([403].includes(error.response.status)) {
        await logout(dispatch, ctx);
      }
    }

    if (error.response && [401].includes(error.response.status) && !retried) {
      return axiosInstance(requestConfig, dispatch, ctx, true);
    }

    throw error;
  }
};

export default axiosInstance;
